export class PriceCalculationHelper {
    /**
     * Recalculates the total price based on the current quantities and prices in the DOM,
     * accounting for service vouchers that cover a certain number of tickets per person type.
     * @param containerSelector The selector for the container holding the price elements.
     * @param totalPriceSelector The selector for the element where the total price will be displayed.
     */
    static recalculateTotalPrice(containerSelector: string, totalPriceSelector: string): void {
        let totalPrice = 0;
        let totalPriceFr = 0;
        let totalQuantity = 0;
        let isCalendarView = false;

        // Get service voucher coverage from data attributes
        const personTypeCoverage = {};
        document.querySelectorAll('[data-service-voucher-coverage]').forEach((element) => {
            const coverageData = JSON.parse(element.getAttribute('data-service-voucher-coverage') || '{}');
            Object.keys(coverageData).forEach(personTypeId => {
                personTypeCoverage[personTypeId] = coverageData[personTypeId];
            });
        });

        // First pass - collect total quantity
        document.querySelectorAll(`${containerSelector} [data-quantity-input]`).forEach((quantityInput) => {
            const quantity = parseInt((quantityInput as HTMLInputElement).value) || 0;
            totalQuantity += quantity;
        });

        // Second pass - calculate prices with voucher discounts
        document.querySelectorAll(`${containerSelector} [data-quantity-input]`).forEach((quantityInput) => {
            const rowId = quantityInput.getAttribute('data-quantity-input');
            const quantity = parseInt((quantityInput as HTMLInputElement).value) || 0;
            const personTypeId = document.querySelector(`[data-ptype-input="${rowId}"]`)?.getAttribute('value');

            // Get voucher coverage for this person type
            const coveredTickets = personTypeId && personTypeCoverage[personTypeId] ? personTypeCoverage[personTypeId] : 0;

            // Check for dynamic pricing from the active calendar div first
            let basePrice = 0;
            const activeDiv = document.querySelector('.axc__row_inner.axc__cell.axc__cell-clickable.axc__row_inner--active');
            if (activeDiv) {
                const dynamicPriceText = activeDiv.querySelector('.ngPriceHolderCalendar')?.textContent?.trim() || '0';
                basePrice = parseFloat(dynamicPriceText.replace(',', '.').replace(/[^\d.]/g, ''));
                isCalendarView = true;
            }

            // If no dynamic price is found, fallback to priceHolder
            if (basePrice === 0) {
                const priceHolder = document.querySelector(`[data-base-price="${rowId}"]`) || document.querySelector(`[data-base-single-price-holder="${rowId}"]`);
                if (priceHolder) {
                    const basePriceText = priceHolder.textContent?.replace(',', '.') || '0';
                    basePrice = parseFloat(basePriceText.replace(/[^\d.]/g, ''));
                }
                isCalendarView = false;
            }

            // Ensure basePrice is valid
            if (isNaN(basePrice) || basePrice <= 0) {
                console.error(`Invalid base price for rowId: ${rowId}`);
                return;
            }

            // Calculate price with voucher discount
            let paidQuantity = Math.max(0, quantity - coveredTickets); // Tickets not covered by the voucher
            let rowTotal = paidQuantity
            let rowTotalFr = paidQuantity;
            if (isCalendarView) {
                rowTotal *= (basePrice / quantity);
            } else {
                rowTotal *= basePrice;
            }

            // Update the row's calculated price
            const calculatedPriceElement = document.querySelector(`[data-calculated-price="${rowId}"]`);
            if (calculatedPriceElement) {
                calculatedPriceElement.textContent = rowTotal.toFixed(2).replace('.', ',');
            }

            // Update foreign currency price if present
            const frcPriceElement = document.querySelector(`[data-calculated-price-frc="${rowId}"]`);
            if (frcPriceElement) {
                const frcBasePriceElement = document.querySelector(`[data-base-price-frc="${rowId}"]`);
                const frcCurrencySymbol = frcBasePriceElement.querySelector('span.labelPrice');
                const frcBasePrice = frcBasePriceElement.textContent.replace(frcCurrencySymbol.textContent, '').trim();
                const basePriceFr = basePrice > 0 ? Number(frcBasePrice.replace(',', '.')) : 0;
                if (isCalendarView) {
                    rowTotalFr *= (basePriceFr / quantity);
                } else {
                    rowTotalFr *= basePriceFr;
                }
                const currencySymbol = frcCurrencySymbol.textContent;
                if (currencySymbol) {
                    frcPriceElement.textContent = `${currencySymbol} ${rowTotalFr.toFixed(2).replace('.', ',')}`;
                }
            }

            // Add to total price
            totalPrice += rowTotal;
            totalPriceFr += rowTotalFr;
        });

        // Update the total price in the DOM - multiple instances
        document.querySelectorAll(totalPriceSelector).forEach(totalPriceElement => {
            totalPriceElement.textContent = totalPrice.toFixed(2).replace('.', ',');
        });

        // Update foreign currency total price elements
        document.querySelectorAll(`${totalPriceSelector}-frc`).forEach(totalFrcElement => {
            const frcText = totalFrcElement.textContent || '';
            const currencySymbol = frcText.split(' ')[0];
            if (currencySymbol) {
                totalFrcElement.textContent = `${currencySymbol} ${totalPriceFr.toFixed(2).replace('.', ',')}`;
            }
        });

        // Create simple summary text based on total quantity
        let summaryText = totalQuantity.toString();
        summaryText += totalQuantity === 1 ? " Ticket" : " Tickets";

        // Update the person type summary with simple ticket count
        document.querySelectorAll('.prod_selection__persontype-summary').forEach(element => {
            element.textContent = summaryText;
        });

        // Enable/disable continue buttons based on whether any ticket is selected
        document.querySelectorAll('.prod_selection__persontype-submit, .prod_selection__prod-continue').forEach(button => {
            button.toggleAttribute('disabled', totalQuantity === 0);
        });
    }

    /**
     * Binds event listeners to quantity inputs to trigger price recalculation on change.
     * @param containerSelector The selector for the container holding the quantity inputs.
     * @param totalPriceSelector The selector for the element where the total price will be displayed.
     */
    static bindQuantityChangeEvents(containerSelector: string, totalPriceSelector: string): void {
        // Create an observer to watch for changes to data-quantity-selection elements
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.target.nodeType === Node.ELEMENT_NODE &&
                    (mutation.target as Element).hasAttribute('data-quantity-selection')) {

                    const rowId = (mutation.target as Element).getAttribute('data-quantity-selection');
                    const newValue = (mutation.target as Element).textContent || "0";

                    // Update the hidden quantity input
                    const quantityInput = document.querySelector(`[data-quantity-input="${rowId}"]`) as HTMLInputElement;
                    if (quantityInput) {
                        quantityInput.value = newValue;

                        // Recalculate prices
                        PriceCalculationHelper.recalculateTotalPrice(containerSelector, totalPriceSelector);
                    }
                }
            });
        });

        // Observe all quantity display elements for changes
        document.querySelectorAll('[data-quantity-selection]').forEach(element => {
            observer.observe(element, {
                childList: true,
                characterData: true,
                subtree: true
            });
        });

        // Also observe the counter buttons for clicks
        document.querySelectorAll('.ax-counter-trigger').forEach(button => {
            button.addEventListener('click', () => {
                // Allow the counter component to update values first
                setTimeout(() => {
                    PriceCalculationHelper.recalculateTotalPrice(containerSelector, totalPriceSelector);
                }, 50);
            });
        });

        // Initial calculation
        PriceCalculationHelper.recalculateTotalPrice(containerSelector, totalPriceSelector);
    }

    /**
     * Initializes service voucher coverage data in the DOM.
     * @param coverageData Object mapping personTypeId to number of tickets covered.
     */
    static initServiceVoucherCoverage(coverageData: Record<string, number>): void {
        // Remove any existing coverage data
        document.querySelectorAll('[data-service-voucher-coverage]').forEach(el => el.remove());

        const dataElement = document.createElement('div');
        dataElement.setAttribute('data-service-voucher-coverage', JSON.stringify(coverageData));
        dataElement.style.display = 'none';
        document.body.appendChild(dataElement);
    }
}